/**
 * Post-sign-in redirect plumbing.
 *
 * ProtectedRoute sends the destination to /sign-in as a `redirect_url` QUERY
 * PARAM rather than router state, because state is lost twice: when Clerk's
 * "Sign up instead" link navigates to /sign-up, and on a page refresh. A query
 * param survives both, plus the OAuth round-trip.
 *
 * Because that value lands on the page that hands out a session, it must be
 * sanitised: only same-origin, path-relative destinations are honoured.
 */

export const REDIRECT_PARAM = 'redirect_url'

/**
 * Embedded control characters (tab, newline, NUL, DEL) can make a value that
 * looks path-relative re-parse as an absolute URL, so reject them outright.
 * Written as a scan rather than a regex literal to keep the source printable.
 */
function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i)
    if (code < 0x20 || code === 0x7f) return true
  }
  return false
}

/**
 * Narrow an untrusted `redirect_url` to a safe in-app path.
 * Accepts "/library/x?share=y#z"; rejects "//evil.com", "https://evil.com",
 * "/\evil.com", "javascript:…" and anything that isn't path-relative.
 */
export function safeRedirect(raw: string | null | undefined, fallback = '/'): string {
  if (!raw) return fallback
  const value = raw.trim()
  // Must be path-relative…
  if (!value.startsWith('/')) return fallback
  // …and not protocol-relative ("//host" / "/\host" — both are read as an
  // absolute host reference by browsers).
  if (value.startsWith('//') || value.startsWith('/\\')) return fallback
  if (hasControlChars(value)) return fallback
  return value
}

/**
 * Full destination (path + query + hash) for a react-router location. Every
 * field is optional so the legacy `state.from` shape (partially typed, may
 * arrive from an older bundle) can be passed straight in; a missing pathname
 * yields '' and safeRedirect turns that into the fallback.
 */
export function locationToPath(loc: { pathname?: string; search?: string; hash?: string }): string {
  if (!loc.pathname) return ''
  return `${loc.pathname}${loc.search ?? ''}${loc.hash ?? ''}`
}

/** Build "/sign-in?redirect_url=…" (or /sign-up), omitting the param for "/". */
export function authUrlWithRedirect(base: '/sign-in' | '/sign-up', to: string): string {
  const safe = safeRedirect(to)
  if (safe === '/') return base
  return `${base}?${REDIRECT_PARAM}=${encodeURIComponent(safe)}`
}

/**
 * One door for "sign in or create an account" (1 Oct 2026): /sign-in in Clerk's
 * sign-in-or-up mode. The person types their email; an existing account asks
 * for its password, a new one carries on into sign-up — so nobody has to guess
 * which button is theirs (picking "Create an account" with an existing email
 * used to end at "That email address is taken").
 */
export function continueUrl(to: string, email?: string): string {
  const u = authUrlWithRedirect('/sign-in', to)
  const q = [`${CONTINUE_PARAM}=1`, ...(email ? [`email=${encodeURIComponent(email)}`] : [])].join('&')
  return `${u}${u.includes('?') ? '&' : '?'}${q}`
}
export const CONTINUE_PARAM = 'continue'
